import { ResponseDto } from '@diritto/common/base/response.dto';
import { ContentContestDto } from '@diritto/common/content/dtos/content-contest.dto';
import { ContentDto } from '@diritto/common/content/dtos/content.dto';
import {
  ContestContentsResponseDto,
  ContestCountResponseDto,
  ContestProfileVoteCountResponseDto,
  ContestVoteContentsReponseDto,
  ContestVoteMessageResponseDto,
  ContestVotePeriodResponseDto,
  ContestVoteResponseDto,
} from '@diritto/common/content/dtos/contest.response.dto';
import {
  ContentContestEditDto,
  ContentContestActiveIdsResponseDto,
  ContentContestResponseDto,
  ContentContestsRequestDto,
  ContentContestsResponseDto,
  ContestStageResponseDto,
} from '@diritto/common/contest/dtos/contest.request.dto';
import { CreateEpisodeDto, UpdateEpisodeDto } from '@diritto/common/episode/dtos/episode-edit.dto';
import { EpisodeEditResponseDto } from '@diritto/common/episode/dtos/episode-edit.response.dto';
import { EpisodeFileResponseDto } from '@diritto/common/episode/dtos/episode-file.response.dto';
import { FancySharesRelayResponseDto } from '@diritto/common/relay/dtos/fancy-share-relay.dtos';
import { RepliesRelayResponseDto } from '@diritto/common/relay/dtos/reply-relay.dtos';
import { Http } from '@diritto/common/utils/requester/http';
import { AxiosRequestConfig } from 'axios';

export class ContestApi {
  constructor(
    protected url: string,
    protected http: Http,
  ) {}

  async addContestContent(
    contestId: number,
    data: ContentContestEditDto,
  ): Promise<{ content: ContentDto; contentContest: ContentContestDto }> {
    const res = await this.http.post<ContentContestEditDto, { content: ContentDto; contentContest: ContentContestDto }>(
      `${this.url}/contests/${contestId}/contents`,
      data,
    );
    return res.data;
  }

  async updateContentContest(contestId: number, contentId: number, data: ContentContestEditDto): Promise<ResponseDto> {
    const res = await this.http.put<ContentContestEditDto, ResponseDto>(
      `${this.url}/contests/${contestId}/contents/${contentId}`,
      data,
    );
    return res.data;
  }

  async getVotePeriod(contestId: number): Promise<ContestVotePeriodResponseDto> {
    const res = await this.http.get<ContestVotePeriodResponseDto>(`${this.url}/contests/${contestId}/vote-period`);
    return res.data;
  }

  async getMyContestContents(contestId: number): Promise<ContentContestsResponseDto> {
    const res = await this.http.get<ContentContestsResponseDto>(`${this.url}/contests/${contestId}/my-contents`);
    return res.data;
  }

  async getApplyCount(contestId: number): Promise<ContestCountResponseDto> {
    const res = await this.http.get<ContestCountResponseDto>(`${this.url}/contests/${contestId}/apply-count`);
    return res.data;
  }

  async getVoteCount(contestId: number): Promise<ContestProfileVoteCountResponseDto> {
    const res = await this.http.get<ContestProfileVoteCountResponseDto>(`${this.url}/contests/${contestId}/vote-count`);
    return res.data;
  }

  async getVoteContents(contestId: number): Promise<ContestVoteContentsReponseDto> {
    const res = await this.http.get<ContestVoteContentsReponseDto>(`${this.url}/contests/${contestId}/vote-contents`);
    return res.data;
  }

  async getContestReplies(contestId: number): Promise<RepliesRelayResponseDto> {
    const res = await this.http.get<RepliesRelayResponseDto>(`${this.url}/relay/contests/${contestId}/replies`);
    return res.data;
  }

  async getContestFancyShares(contestId: number): Promise<FancySharesRelayResponseDto> {
    const res = await this.http.get<FancySharesRelayResponseDto>(
      `${this.url}/relay/contests/${contestId}/fancy-shares`,
    );
    return res.data;
  }

  async getVoteMessage(contestId: number): Promise<ContestVoteMessageResponseDto> {
    const res = await this.http.get<ContestVoteMessageResponseDto>(`${this.url}/contests/${contestId}/vote-message`);
    return res.data;
  }

  async vote(contestId: number, contentId: number): Promise<ContestVoteResponseDto> {
    const res = await this.http.post<undefined, ContestVoteResponseDto>(
      `${this.url}/contests/${contestId}/contents/${contentId}/vote`,
    );
    return res.data;
  }

  async addMessageToVote(voteId: number, message: string): Promise<ResponseDto> {
    const res = await this.http.post<{ message: string }, ResponseDto>(`${this.url}/contests/votes/${voteId}/message`, {
      message,
    });
    return res.data;
  }

  async findByContestAndKeywords(contestId: number, keywords?: string[]) {
    const res = await this.http.get<ContestContentsResponseDto>(`${this.url}/contests/${contestId}/contents`, {
      params: {
        keywords,
      },
    });
    return res.data;
  }

  async getContentContest(
    contestId: number,
    contentId: number,
    options?: AxiosRequestConfig,
  ): Promise<ContentContestResponseDto> {
    const res = await this.http.get<ContentContestResponseDto>(
      `${this.url}/contests/${contestId}/contents/${contentId}`,
      options,
    );
    return res.data;
  }

  async getContestStage(contestId: number): Promise<ContestStageResponseDto> {
    const res = await this.http.get<ContestStageResponseDto>(`${this.url}/contests/${contestId}/stage`);
    return res.data;
  }

  async createContestEpisode(contestId: number, data: CreateEpisodeDto): Promise<EpisodeEditResponseDto> {
    const res = await this.http.post<CreateEpisodeDto, EpisodeEditResponseDto>(
      `${this.url}/contests/${contestId}/episodes/edit`,
      data,
    );
    return res.data;
  }

  async updateContestEpisode(
    contestId: number,
    episodeId: number,
    data: UpdateEpisodeDto,
  ): Promise<EpisodeEditResponseDto> {
    const res = await this.http.put<UpdateEpisodeDto, EpisodeEditResponseDto>(
      `${this.url}/contests/${contestId}/episodes/edit/${episodeId}`,
      data,
    );
    return res.data;
  }

  async uploadContestEpisodeFiles(contestId: number, data: FormData) {
    const res = await this.http.post<FormData, EpisodeFileResponseDto>(
      `${this.url}/contests/${contestId}/episodes/edit/files`,
      data,
    );
    return res.data;
  }

  async getActiveContentContestIds(
    contestId: number,
    data: ContentContestsRequestDto,
    options?: AxiosRequestConfig,
  ): Promise<ContentContestActiveIdsResponseDto> {
    const res = await this.http.post<ContentContestsRequestDto, ContentContestActiveIdsResponseDto>(
      `${this.url}/contests/${contestId}/active-content-contest-ids`,
      data,
      options,
    );
    return res.data;
  }
}
